import { WixPatternsEssentials, DataCapsule } from '../model';
import {
  getGlobalSettings,
  setGlobalSettings,
} from '@wix/bex-utils/@wix/ambassador-app-settings-v1-settings/http';
import { Host } from '@wix/bex-utils/@wix/ambassador-app-settings-v1-settings/types';
import { QueryCache, QueryClient } from 'react-query/core';

const queryName = 'app-settings-capsule';

export interface AppSettingsCapsuleParams {
  readonly httpClient: WixPatternsEssentials['httpClient'];
  readonly context: {
    namespace: string;
    componentId: string;
    host: Host;
  };
  queryCache: QueryCache;
}

export class AppSettingsCapsule implements DataCapsule {
  readonly httpClient;
  readonly context;
  readonly queryClient;
  readonly queryCache;

  constructor(params: AppSettingsCapsuleParams) {
    this.httpClient = params.httpClient;
    this.context = params.context;
    this.queryCache = params.queryCache;
    this.queryClient = new QueryClient({
      queryCache: this.queryCache,
    });
  }

  _buildAppSettingsPayload<P>(payload: P) {
    const {
      context: { componentId },
    } = this;

    return {
      ...payload,
      ...(componentId && { componentId }),
    };
  }

  _getQueryKey() {
    return [queryName, this.context];
  }

  async getItem<T>(key: string) {
    const { context, httpClient, queryClient } = this;
    const { data } = await queryClient.fetchQuery({
      queryFn: () =>
        httpClient.request(
          getGlobalSettings({
            host: context.host,
          }),
        ),
      queryKey: this._getQueryKey(),
      staleTime: 120000,
    });
    return data.settings?.[context.componentId]?.[context.namespace]?.[
      key
    ] as unknown as T;
  }

  async setItem<T>(key: string, value: T) {
    const { httpClient, queryCache, context } = this;

    queryCache
      .findAll({ queryKey: this._getQueryKey() })
      .forEach((q) => q.invalidate());

    await httpClient.request(
      setGlobalSettings({
        host: context.host,
        settings: {
          [context.componentId]: {
            [context.namespace]: {
              [key]: value,
            },
          },
        },
      }),
    );
  }

  async removeItem(key: string): Promise<void> {
    await this.setItem(key, null);
  }
}
